/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
} from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { v4 as uuidv4 } from 'uuid';
import useStyles from '../styles';
import CustomButton from '../../../components/General/CustomButton';
import useFetchApi from '../../../hooks/useFetchApi';
import { GetSpeciesResponse, GetVarietyByIdResponse } from '../../../requests/api/apiTypes';
import { GetPhenologicalStateNamesResponse } from '../../../requests/api/apiClient';
import MonthDayPicker from '../../../components/General/MonthDayPicker';
import {
  PhenologicalStateChecker,
  LocalPhenologicalStates,
  VarietyPhenologicalStateInput,
} from '../../../utils/varieties';

type Errors = {
  [key: string]: {
    message: string,
    active?: boolean,
    stateIds?: string[],
  }
};

type CatalogOption = { id: string, name: string };

const PhenologicalStateComponent = ({
  elem, catalogOptions, onValueChange, error, removePhenologicalState,
}: {
  elem: LocalPhenologicalStates,
  catalogOptions: CatalogOption[],
  onValueChange: (
    id: string,
    value: string | number,
    source: 'phenologicalStateNameId' | 'startDate' | 'endDate' | 'kcMin' | 'kcMax',
  ) => void,
  error: Errors,
  removePhenologicalState: (item: string) => void,
}) => (
  <TableRow >
    <TableCell>
      <TextField
        label='Nombre'
        name="phenologicalStateNameId"
        variant='standard'
        value={elem.phenologicalStateNameId ?? ''}
        onChange={(e) => onValueChange(elem.id, e.target.value, 'phenologicalStateNameId')}
        select
        required
        error={error.phenologicalStateNameId.stateIds?.includes(elem.id)}
        helperText={
          error.phenologicalStateNameId.stateIds?.includes(elem.id)
            ? error.phenologicalStateNameId.message : ''
        }
        sx={{
          minWidth: '150px',
          width: '100%',
        }}
      >
        {catalogOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
    </TableCell>
    <TableCell>
      <MonthDayPicker
        label="Inicio"
        value={elem.startDate ?? ''}
        onChange={(value: string) => onValueChange(elem.id, value, 'startDate')}
        error={error.startDate.stateIds?.includes(elem.id) ?? false}
        helperText={error.startDate.stateIds?.includes(elem.id) ? error.startDate.message : 'Formato: MM-DD'}
      />
    </TableCell>
    <TableCell>
      <MonthDayPicker
        label="Fin"
        value={elem.endDate ?? ''}
        onChange={(value: string) => onValueChange(elem.id, value, 'endDate')}
        error={error.endDate.stateIds?.includes(elem.id) ?? false}
        helperText={error.endDate.stateIds?.includes(elem.id) ? error.endDate.message : 'Formato: MM-DD'}
      />
    </TableCell>
    <TableCell>
      <TextField
        label="Kc Min"
        type="number"
        variant="standard"
        value={elem.kcMin || ''}
        onChange={(e) => onValueChange(elem.id, e.target.value, 'kcMin')}
        inputProps={{
          step: 0.1,
          min: 0,
        }}
        sx={{ minWidth: '40px', width: '80px' }}
        error={error.kcMin.stateIds?.includes(elem.id) ?? false}
        helperText={error.kcMin.stateIds?.includes(elem.id) ? error.kcMin.message : ''}
      />
    </TableCell>
    <TableCell>
      <TextField
        label="Kc Max"
        type="number"
        variant="standard"
        value={elem.kcMax || ''}
        onChange={(e) => onValueChange(elem.id, e.target.value, 'kcMax')}
        inputProps={{
          step: 0.1,
          min: 0,
        }}
        sx={{ minWidth: '40px', width: '80px' }}
        error={error.kcMax.stateIds?.includes(elem.id) ?? false}
        helperText={error.kcMax.stateIds?.includes(elem.id) ? error.kcMax.message : ''}
      />
    </TableCell>
    <TableCell >
      <IconButton onClick={() => removePhenologicalState(elem.id)}>
        <DeleteIcon />
      </IconButton>
    </TableCell>
  </TableRow>
);

const UpsertModalVarietyContent = ({
  handleCloseModal,
  handleUpsertFunction,
  id,
  isLoadingUpsert,
}: {
  handleCloseModal: VoidFunction,
  handleUpsertFunction: ({
    name,
    speciesId,
    phenologicalStates,
    id,
  }: {
    name: string,
    speciesId: string,
    phenologicalStates: VarietyPhenologicalStateInput[],
    id?: string,
  }) => void,
  id?: string,
  isLoadingUpsert: boolean,
}) => {
  const classes = useStyles();

  const [varietyName, setVarietyName] = useState('');
  const [speciesId, setSpeciesId] = useState('');
  const [localPhenologicalStates, setLocalPhenologicalStates] = useState<LocalPhenologicalStates[]
  >([]);
  const [beenUpdated, setBeenUpdated] = useState(false);
  const [speciesErrors, setSpeciesErrors] = useState({
    varietyName: {
      message: '',
      active: false,
    },
    speciesName: {
      message: '',
      active: false,
    },
  });

  const [phenologicalStateErrors, setPhenologicalStateErrors] = useState({
    phenologicalStateNameId: {
      message: '',
      stateIds: [] as string[],
    },
    startDate: {
      message: '',
      stateIds: [] as string[],
    },
    endDate: {
      message: '',
      stateIds: [] as string[],
    },
    kcMin: {
      message: '',
      stateIds: [] as string[],
    },
    kcMax: {
      message: '',
      stateIds: [] as string[],
    },
  });

  const { data } = useFetchApi<GetSpeciesResponse>({
    modelName: 'species',
    modelEndpoint: 'speciesList',
  });

  const { data: catalogData } = useFetchApi<GetPhenologicalStateNamesResponse>({
    modelName: 'phenologicalStateName',
    modelEndpoint: 'phenologicalStateNameList',
  });
  const catalogOptions: CatalogOption[] = catalogData?.phenologicalStateNames ?? [];

  const entityToModify = id && useFetchApi<GetVarietyByIdResponse>({
    modelName: 'varieties',
    modelEndpoint: 'varietiesDetail',
    id,
  });

  useEffect(() => {
    if (id && entityToModify && entityToModify.data && !beenUpdated) {
      const auxPhenologicalStates = entityToModify.data.variety?.phenologicalStates
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.map((elem: any) => ({
          startDate: elem.startDate,
          endDate: elem.endDate,
          name: elem.name ?? '',
          phenologicalStateNameId: elem.phenologicalStateNameId
            ? String(elem.phenologicalStateNameId) : '',
          kcMin: elem.kcMin,
          kcMax: elem.kcMax,
          id: uuidv4(),
        })) ?? [];
      setBeenUpdated(true);
      setVarietyName(entityToModify.data.variety?.name ?? '');
      setSpeciesId(entityToModify.data.variety?.speciesId ?? '');
      setLocalPhenologicalStates(auxPhenologicalStates);
    }
  }, [entityToModify]);

  const isPhenologicalStateComplete = (phenologicalState: LocalPhenologicalStates) => {
    if (!phenologicalState.phenologicalStateNameId
        || phenologicalState.phenologicalStateNameId.trim() === '') {
      setPhenologicalStateErrors((prevState) => ({
        ...prevState,
        phenologicalStateNameId: {
          stateIds: [...prevState.phenologicalStateNameId.stateIds, phenologicalState.id],
          message: 'Debes seleccionar un estado fenológico del catálogo',
        },
      }));
      return false;
    }

    if (!phenologicalState.startDate) {
      setPhenologicalStateErrors((prevState) => ({ ...prevState, startDate: { stateIds: [...prevState.startDate.stateIds, phenologicalState.id], message: 'Fecha de inicio no puede estar vacia' } }));
      return false;
    }

    if (!phenologicalState.endDate) {
      setPhenologicalStateErrors((prevState) => ({ ...prevState, endDate: { stateIds: [...prevState.endDate.stateIds, phenologicalState.id], message: 'Fecha de fin no puede estar vacia' } }));
      return false;
    }

    return true;
  };

  const handleAddPhenologicalState = () => {
    let allValid = true;

    localPhenologicalStates.forEach((state) => {
      const isValid = isPhenologicalStateComplete(state);
      if (!isValid) {
        allValid = false;
      }
    });

    if (allValid) {
      setLocalPhenologicalStates((prevPhenologicalStates) => [
        ...prevPhenologicalStates,
        {
          id: uuidv4(),
          phenologicalStateNameId: '',
          name: '',
          startDate: '01-01',
          endDate: '01-02',
          kcMin: undefined,
          kcMax: undefined,
        },
      ]);
    }
  };

  const removePhenologicalState = (phenologicalStateId: string) => {
    setLocalPhenologicalStates((prevState) => prevState
      .filter((elem) => elem.id !== phenologicalStateId));
  };

  const onValueChange = (
    phenStateId: string,
    value: string | number,
    source: keyof typeof phenologicalStateErrors,
  ) => {
    setLocalPhenologicalStates((prevStates) => prevStates.map((state) => {
      if (state.id !== phenStateId) return state;
      if (source === 'phenologicalStateNameId') {
        const match = catalogOptions.find((c) => c.id === value);
        return { ...state, phenologicalStateNameId: String(value), name: match?.name ?? '' };
      }
      return { ...state, [source]: value };
    }));

    if (source !== 'kcMin' && source !== 'kcMax') {
      setPhenologicalStateErrors((prevState) => {
        const otherSource: 'startDate' | 'endDate' | null = source === 'startDate'
          ? 'endDate'
          : source === 'endDate' ? 'startDate' : null;

        const next = {
          ...prevState,
          [source]: {
            ...prevState[source],
            stateIds: prevState[source].stateIds.filter(
              (stateId: string) => stateId !== phenStateId,
            ),
          },
        };

        if (otherSource) {
          next[otherSource] = {
            ...prevState[otherSource],
            stateIds: prevState[otherSource].stateIds.filter(
              (stateId: string) => stateId !== phenStateId,
            ),
          };
        }

        return next;
      });
    }
  };

  const handleSubmitUpsert = () => {
    if (speciesId.trim() === '') {
      setSpeciesErrors((prevState) => ({ ...prevState, speciesName: { message: 'Debes elegir una especia a asociar', active: true } }));
      return;
    }

    if (varietyName.trim() === '') {
      setSpeciesErrors((prevState) => ({ ...prevState, varietyName: { message: 'Nombre no puede estar vacío', active: true } }));
      return;
    }

    let allValid = true;
    localPhenologicalStates.forEach((state) => {
      if (state.kcMin && state.kcMax) {
        if (!PhenologicalStateChecker.isKcValid(state.kcMin, state.kcMax) && allValid) {
          setPhenologicalStateErrors((prevState) => ({
            ...prevState,
            kcMin: {
              stateIds: [...prevState.kcMin.stateIds, state.id],
              message: 'Kc mínimo debe ser menor o igual al Kc máximo',
            },
          }));
          allValid = false;
        }
      }
    });
    if (!allValid) {
      return;
    }

    const hasOverlap = PhenologicalStateChecker.isDateOverlap(localPhenologicalStates);

    if (hasOverlap) {
      const hasOverlapMessage = 'Fechas se superponen a estados anteriores';
      const lastPhenologicalId = localPhenologicalStates.at(-1)?.id;
      if (lastPhenologicalId) {
        setPhenologicalStateErrors((prevState) => (
          {
            ...prevState,
            startDate: {
              stateIds: [
                ...prevState.startDate.stateIds,
                lastPhenologicalId,
              ],
              message: hasOverlapMessage,
            },
          }));
        setPhenologicalStateErrors(
          (prevState) => (
            {
              ...prevState,
              endDate: {
                stateIds: [
                  ...prevState.startDate.stateIds,
                  lastPhenologicalId,
                ],
                message: hasOverlapMessage,
              },
            }),
        );
      }

      return;
    }

    localPhenologicalStates.forEach((state) => {
      const isValid = isPhenologicalStateComplete(state);
      if (!isValid) {
        allValid = false;
      }
    });

    if (!allValid) {
      return;
    }

    handleUpsertFunction({
      name: varietyName,
      speciesId,
      phenologicalStates: localPhenologicalStates
        .filter((elem): elem is LocalPhenologicalStates & { phenologicalStateNameId: string } => (
          !!elem.phenologicalStateNameId
        ))
        .map((elem) => ({
          phenologicalStateNameId: elem.phenologicalStateNameId,
          startDate: elem.startDate as string,
          endDate: elem.endDate as string,
          kcMin: elem.kcMin,
          kcMax: elem.kcMax,
        })),
      id,
    });
  };

  return (
    <Box className={classes.upsertVarietyModalContainer}>
      <Box className={classes.upsertVarietyModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.upsertVarietyModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            {id ? 'Editar variedad' : 'Agregar variedad'}
          </span>
        </Typography>
        <IconButton onClick={handleCloseModal}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider className={classes.dividerStyle} />
      <Box className={classes.upsertVarietyNameSpeciesContainer}>
        <Box width='100%' marginRight='10px'>
          <Autocomplete
            options={data?.species ?? []}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={data?.species?.find((s) => s.id === speciesId) || null}
            onChange={(_, newValue) => {
              setSpeciesErrors((prevState) => ({ ...prevState, speciesName: { message: '', active: false } }));
              setSpeciesId(newValue?.id ?? '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant='standard'
                label='Especie'
                fullWidth
                required
                error={speciesErrors.speciesName.active}
                helperText={speciesErrors.speciesName.active && speciesErrors.speciesName.message}
              />
            )}
          />
        </Box>
        <Box width='100%' marginLeft='10px'>
          <TextField
            variant='standard'
            label='Variedad'
            fullWidth
            required
            error={speciesErrors.varietyName.active}
            helperText={speciesErrors.varietyName.active && speciesErrors.varietyName.message}
            value={varietyName}
            onChange={(e) => {
              setSpeciesErrors((prevState) => ({ ...prevState, varietyName: { message: '', active: false } }));
              setVarietyName(e.target.value);
            }}
          />
        </Box>

      </Box>
      <Box className={classes.upsertVarietyModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.upsertVarietyModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            Estados fenológicos
          </span>
        </Typography>
        {/* added disabled button to align titles */}
        <IconButton disabled />
      </Box>
      <PerfectScrollbar>
        <TableContainer className={classes.tableScrollbarContainer} >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>
                  Nombre Estado
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  Fecha inicio
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  Fecha fin
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  Kc Min
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  Kc Max
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {localPhenologicalStates.map((elem) => (
                <PhenologicalStateComponent
                  key={elem.id}
                  elem={elem}
                  catalogOptions={catalogOptions}
                  onValueChange={onValueChange}
                  error={phenologicalStateErrors}
                  removePhenologicalState={removePhenologicalState}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PerfectScrollbar>
      <CustomButton color='blue' buttonText='+ Agregar estado fenológico' onClick={handleAddPhenologicalState} />
      <Box className={classes.upsertVarietyModalButtonsContainer}>
        <Box width='100%' mr={2}><CustomButton color='blue' buttonText='Cancelar' onClick={handleCloseModal} /></Box>
        <Box width='100%' ml={2}>
          <CustomButton
            color='green'
            buttonText={id ? 'Editar' : 'Agregar'}
            onClick={() => handleSubmitUpsert()}
            disabled={isLoadingUpsert}
            setLoadingSpinnerWhenDisabled={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default UpsertModalVarietyContent;
